import { useState } from "react";
import { useNavigate } from "react-router-dom";
import QuickInterventionDialog from "@/components/QuickInterventionDialog";
import EditInterventionDialog from "@/components/EditInterventionDialog";
import { ArrowRight, Clock } from "@phosphor-icons/react";
import { isoDay, ivColor } from "./dateHelpers";
import { CategorieBadge } from "@/components/InterventionCategoryPicker";

// Google-Calendar style vertical grid, 7h → 19h, 30-min slots.
export default function GrilleHoraireView({ days, interventions, team, chantiersMap, onChange }) {
  const nav = useNavigate();
  const [editingIv, setEditingIv] = useState(null);
  const [quickTarget, setQuickTarget] = useState(null);

  const START_H = 7;
  const END_H = 19;
  const SLOT_MIN = 30;
  const PX_PER_HOUR = 56;
  const totalHours = END_H - START_H;
  const totalPx = totalHours * PX_PER_HOUR;
  const slots = Array.from({ length: totalHours * (60 / SLOT_MIN) }, (_, i) => i);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const teamById = Object.fromEntries(team.map((u) => [u.id, u]));

  const dayColumns = days.map((d) => {
    const iso = isoDay(d);
    const items = interventions
      .filter((x) => (x.scheduled_date || "").slice(0, 10) === iso)
      .map((iv) => {
        const timePart = (iv.scheduled_date || "").slice(11, 16);
        const [hh, mm] = timePart.split(":").map((n) => parseInt(n, 10) || 0);
        const startMin = hh * 60 + mm;
        const gridStartMin = START_H * 60;
        const gridEndMin = END_H * 60;
        const rawTopMin = startMin - gridStartMin;
        const durMin = Math.max(15, iv.duration_min || 60);
        const rawEndMin = startMin + durMin - gridStartMin;
        const topMin = Math.max(0, rawTopMin);
        const endMin = Math.min(gridEndMin - gridStartMin, rawEndMin);
        const visibleHeightMin = Math.max(20, endMin - topMin);
        return {
          iv,
          topPx: (topMin / 60) * PX_PER_HOUR,
          heightPx: (visibleHeightMin / 60) * PX_PER_HOUR,
          overflowsBefore: rawTopMin < 0,
          overflowsAfter: rawEndMin > (gridEndMin - gridStartMin),
        };
      })
      .sort((a, b) => a.topPx - b.topPx);

    // Lane-packing for overlaps
    const lanes = [];
    const withLanes = items.map((it) => {
      const startPx = it.topPx;
      const endPx = it.topPx + it.heightPx;
      let laneIdx = lanes.findIndex((laneEnd) => startPx >= laneEnd - 0.5);
      if (laneIdx === -1) {
        lanes.push(endPx);
        laneIdx = lanes.length - 1;
      } else {
        lanes[laneIdx] = endPx;
      }
      return { ...it, lane: laneIdx };
    });
    const laneCount = Math.max(1, lanes.length);
    return { day: d, iso, items: withLanes, laneCount };
  });

  const now = new Date();
  const nowMinFromStart = now.getHours() * 60 + now.getMinutes() - START_H * 60;
  const nowTopPx = (nowMinFromStart / 60) * PX_PER_HOUR;
  const nowIsInGrid = nowMinFromStart >= 0 && nowMinFromStart <= totalHours * 60;

  return (
    <>
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden shadow-lg shadow-black/20" data-testid="grille-horaire">
        <div className="overflow-x-auto no-scrollbar">
          <div className="min-w-[900px]">
            <div className="grid border-b border-white/10" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
              <div className="p-2 text-[9px] uppercase tracking-widest text-white/30 text-center">HR</div>
              {dayColumns.map(({ day: d }) => {
                const isToday = d.getTime() === today.getTime();
                return (
                  <div key={d.toISOString()} className={`p-2 text-center border-l border-white/10 ${isToday ? "bg-[#005CFF]/10" : ""}`}>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</div>
                    <div className={`font-display font-bold text-base ${isToday ? "text-[#005CFF]" : ""}`}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>

            <div className="grid relative" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
              <div className="relative border-r border-white/10" style={{ height: `${totalPx}px` }}>
                {slots.map((i) => {
                  const h = START_H + Math.floor(i / 2);
                  const m = i % 2 === 0 ? "00" : "30";
                  const showLabel = m === "00";
                  return (
                    <div
                      key={`t${i}`}
                      className={`absolute left-0 right-0 px-1 text-right text-[9px] tabular-nums ${showLabel ? "text-white/60" : "text-white/20"}`}
                      style={{ top: `${i * (PX_PER_HOUR / 2)}px`, height: `${PX_PER_HOUR / 2}px`, lineHeight: "10px" }}
                    >
                      {showLabel ? `${h}h` : ""}
                    </div>
                  );
                })}
              </div>

              {dayColumns.map(({ day: d, iso, items, laneCount }) => {
                const isToday = d.getTime() === today.getTime();
                return (
                  <div key={iso} className={`relative border-l border-white/10 ${isToday ? "bg-[#005CFF]/5" : ""}`} style={{ height: `${totalPx}px` }} data-testid={`grille-col-${iso}`}>
                    {slots.map((i) => (
                      <div
                        key={`gl-${iso}-${i}`}
                        className={`absolute left-0 right-0 border-t ${i % 2 === 0 ? "border-white/10" : "border-white/5"}`}
                        style={{ top: `${i * (PX_PER_HOUR / 2)}px` }}
                        onClick={() => {
                          if (!team || team.length === 0) return;
                          const h = START_H + Math.floor(i / 2);
                          const m = i % 2 === 0 ? "00" : "30";
                          const first = team[0];
                          setQuickTarget({
                            technicianId: first.id,
                            technicianName: first.name,
                            dateIso: iso,
                            time: `${String(h).padStart(2, "0")}:${m}`,
                          });
                        }}
                      />
                    ))}

                    {isToday && nowIsInGrid && (
                      <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: `${nowTopPx}px` }}>
                        <div className="h-[2px] bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]" />
                        <div className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-red-500" />
                      </div>
                    )}

                    {items.map(({ iv, topPx, heightPx, lane, overflowsBefore, overflowsAfter }) => {
                      const u = teamById[iv.technician_id];
                      const ch = (chantiersMap || {})[iv.chantier_id];
                      const color = ivColor(iv, chantiersMap, u);
                      const laneWidthPct = 100 / laneCount;
                      const timePart = (iv.scheduled_date || "").slice(11, 16);
                      const endMin = (parseInt(timePart.split(":")[0], 10) || 0) * 60 + (parseInt(timePart.split(":")[1], 10) || 0) + (iv.duration_min || 60);
                      const endHH = String(Math.floor(endMin / 60) % 24).padStart(2, "0");
                      const endMM = String(endMin % 60).padStart(2, "0");
                      return (
                        <button
                          key={iv.id}
                          onClick={() => setEditingIv(iv)}
                          className="absolute rounded-md text-left overflow-hidden group hover:z-20 hover:ring-2 hover:ring-white/40 hover:shadow-lg transition-all"
                          style={{
                            top: `${topPx + 1}px`,
                            height: `${heightPx - 2}px`,
                            left: `calc(${lane * laneWidthPct}% + 2px)`,
                            width: `calc(${laneWidthPct}% - 4px)`,
                            background: color + "33",
                            borderLeft: `3px solid ${color}`,
                          }}
                          data-testid={`grille-iv-${iv.id}`}
                          title={`${iv.title} · ${timePart}–${endHH}:${endMM} · ${u?.name || ""}`}
                        >
                          <div className="p-1.5 h-full flex flex-col">
                            <div className="flex items-center gap-1 text-[9px] font-mono tabular-nums text-white/80">
                              {overflowsBefore && <span className="text-red-400">↑</span>}
                              {timePart}–{endHH}:{endMM}
                              {iv.type === "depannage" && <span className="ml-auto text-[8px] bg-amber-500 text-black px-1 rounded font-bold">SAV</span>}
                            </div>
                            <div className="flex items-center gap-1 min-w-0">
                              <CategorieBadge value={iv.categorie} size="xs" showLabel={false} />
                              <div className="text-[11px] font-medium truncate leading-tight flex-1">{iv.title}</div>
                            </div>
                            {heightPx > 36 && (
                              <div className="text-[9px] text-white/60 truncate leading-tight">{u?.name}</div>
                            )}
                            {heightPx > 52 && ch && (
                              <div className="text-[9px] text-white/50 truncate leading-tight">{ch.name}</div>
                            )}
                            {overflowsAfter && <div className="text-[8px] text-red-400 text-right mt-auto">↓</div>}
                          </div>
                          {iv.chantier_id && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); nav(`/chantiers/${iv.chantier_id}`); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); nav(`/chantiers/${iv.chantier_id}`); } }}
                              className="absolute top-0.5 right-0.5 w-4 h-4 rounded flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              title="Ouvrir la fiche chantier"
                              data-testid={`grille-open-ch-${iv.id}`}
                            >
                              <ArrowRight size={9} weight="bold" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="p-2 text-[10px] text-white/40 border-t border-white/5 flex items-center gap-2">
          <Clock size={11} /> Créneaux {START_H}h → {END_H}h · pas de 30 min · clique une case vide pour créer
        </div>
      </div>

      <EditInterventionDialog
        open={!!editingIv}
        onOpenChange={(o) => !o && setEditingIv(null)}
        intervention={editingIv}
        team={team}
        chantiersById={chantiersMap}
        onChanged={onChange}
      />
      <QuickInterventionDialog
        open={!!quickTarget}
        onOpenChange={(o) => !o && setQuickTarget(null)}
        technicianId={quickTarget?.technicianId}
        technicianName={quickTarget?.technicianName}
        dateIso={quickTarget?.dateIso}
        time={quickTarget?.time}
        onCreated={onChange}
      />
    </>
  );
}
